/**
 * useCaseScreenshots — the case-screenshot state cluster of File Detail
 * (decomposition step F4, CLAUDE.md §8.4; the verification flow itself is
 * the screenshot-verification section and caseScreenshotsApi).
 *
 * Owns the state that only this cluster reads — the per-case rows
 * (`caseScreenshots`), the per-row upload index / stage / error maps, the
 * lightbox URL, the flag-detail and override modal state, the Adviser's
 * verification diagnostic with its dismissed flag and the aggregate
 * diagnostics modal flag — and the five handlers that write it:
 * `reloadCaseScreenshots`, the legacy single-screenshot upload
 * (`handleScreenshotUpload`), the per-case upload with verification
 * (`handleCaseScreenshotUpload`), the Adviser's Layer 2 override
 * (`handleOverrideUpload`) and the Layer 3 flag clear (`handleClearFlag`),
 * plus the derived values the panel, the grid and the modals read.
 *
 * It never builds a verification request: `handleCaseScreenshotUpload`
 * calls `caseScreenshotsApi.uploadCaseScreenshot` with the same fields
 * the view passed, and that module calls /api/verify-screenshot. Role is
 * not an input: every Adviser gate sits at a render site in the view
 * (`isAdviser` on the panel and the flag modal, the Cases card's Edit
 * button, the diagnostics banner), so no gate moved.
 *
 * Two setters are exposed for the view's load path only: FileDetail's
 * `loadFile` fetches the rows and (for the Adviser) the latest
 * verification event alongside the file row and writes them through
 * `setCaseScreenshots` / `setVerificationDiagnostic`, exactly as before.
 * `handleApplyCaseStructure` and `showEditCasesModal` stay in the view
 * because that write goes through `loadFile` and changes `caseCount` /
 * `caseLabels`, which the memo, the Cases card and the summary read.
 *
 * The handler bodies are FileDetail's, moved verbatim; `file`, `id`,
 * `user`, `setFile`, `setError` and `setActionLoading` arrive as inputs
 * under the names the bodies already used.
 */

import { useState, useCallback } from 'react';
import { updateFile } from '../api/filesApi';
import { uploadAtworkScreenshot } from '../api/storageApi';
import {
  getCaseScreenshotsForFile,
  uploadCaseScreenshot,
  overrideFlag,
  CaseScreenshotVerificationError,
} from '../api/caseScreenshotsApi';

export default function useCaseScreenshots({ file, id, user, setFile, setError, setActionLoading }) {
  // Multi-case screenshot state (only relevant when file.caseCount != null).
  // `caseScreenshots` is an array of rows from case_screenshots, sorted by
  // case_index. `screenshotsRefresh` is bumped after a successful upload to
  // trigger a refetch. `caseUploadingIndex` tracks the row that's currently
  // uploading so we can show a per-row spinner.
  const [caseScreenshots, setCaseScreenshots] = useState([]);
  const [caseUploadingIndex, setCaseUploadingIndex] = useState(null);
  // Per-row upload UX state machine: 'uploading' (file transfer / API call
  // pending) → cleared on result. Keyed by case index so each row can
  // animate independently.
  const [caseUploadStage, setCaseUploadStage] = useState({});
  // Per-row inline error from a Layer 2 hard reject. Cleared when the
  // user picks a new file or dismisses.
  const [caseRowErrors, setCaseRowErrors] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Modals around the verification flow. flagModalCase holds the
  // mapRow object whose flag the user clicked to inspect; overrideModal
  // holds the Layer 2 reject context when the advisor wants to override.
  const [flagModalCase, setFlagModalCase] = useState(null);
  const [overrideModal, setOverrideModal] = useState(null);
  // Advisor-only diagnostic banner — most recent verification system
  // event linked to this file in the last 48 hours, plus the dismissed
  // flag (UI-only, resets on next failure).
  const [verificationDiagnostic, setVerificationDiagnostic] = useState(null);
  const [diagnosticDismissed, setDiagnosticDismissed] = useState(false);
  const [showAggregateDiagnostics, setShowAggregateDiagnostics] = useState(false);

  // Refetch just the case_screenshots rows after an upload or structure edit,
  // without reloading the entire file row.
  const reloadCaseScreenshots = useCallback(async (fileRowId) => {
    try {
      const shots = await getCaseScreenshotsForFile(fileRowId);
      setCaseScreenshots(shots);
    } catch (err) {
      console.warn('Failed to refresh case screenshots:', err);
    }
  }, []);

  // Upload atwork screenshot
  const handleScreenshotUpload = async (e) => {
    const screenshot = e.target.files[0];
    if (!screenshot) return;

    try {
      setActionLoading(true);
      const url = await uploadAtworkScreenshot( file.fileId, screenshot);
      const updated = await updateFile( id, { atworkScreenshotUrl: url });
      setFile(updated);
    } catch (err) {
      console.error('Screenshot upload failed:', err);
      setError('Failed to upload screenshot');
    } finally {
      setActionLoading(false);
    }
  };

  // Upload (or replace) a single case's AtWork screenshot. Runs the
  // verification flow: Layer 2 fail → inline error on the row (and
  // an override path for advisors), Layer 3 flag → save with flag,
  // pass → save normally.
  const handleCaseScreenshotUpload = async (caseIndex, e, options = {}) => {
    const picked = options.fileBlob || (e && e.target.files && e.target.files[0]);
    if (!picked) return;
    if (e && e.target) {
      // Reset the input so picking the same file twice in a row still
      // fires a change event (replace-with-same-file would otherwise noop).
      e.target.value = '';
    }

    // Clear any prior per-row error and tick the upload progress state.
    setCaseRowErrors((prev) => {
      const next = { ...prev };
      delete next[caseIndex];
      return next;
    });
    setCaseUploadingIndex(caseIndex);
    setCaseUploadStage((prev) => ({ ...prev, [caseIndex]: 'uploading' }));

    try {
      // Brief switch to "Verifying…" once the file is in flight. The
      // verify endpoint typically returns in 2–4 seconds; this gives
      // the user a clear two-step indicator.
      setTimeout(
        () => setCaseUploadStage((prev) => ({ ...prev, [caseIndex]: 'verifying' })),
        300
      );

      await uploadCaseScreenshot({
        fileRowId: file.id,
        caseCount: file.caseCount,
        caseIndex,
        clientName: file.clientName,
        caseLabel: (file.caseLabels || [])[caseIndex] || '',
        file: picked,
        uploadedBy: user?.email || '',
        override: !!options.override,
        overrideReason: options.overrideReason || null,
      });
      await reloadCaseScreenshots(file.id);
      // Refresh advisor diagnostic — a successful upload may resolve a
      // prior outage, in which case the banner stays until the 48h
      // window expires (still useful as a record of past failure).
    } catch (err) {
      if (err instanceof CaseScreenshotVerificationError) {
        setCaseRowErrors((prev) => ({
          ...prev,
          [caseIndex]: {
            kind: err.kind,
            message: err.message,
            details: err.details,
            // Cache the rejected file so the advisor's override path
            // can re-submit it without re-picking from disk.
            cachedFile: picked,
          },
        }));
      } else {
        console.error('Case screenshot upload failed:', err);
        setError(err.message || 'Failed to upload case screenshot');
      }
    } finally {
      setCaseUploadingIndex(null);
      setCaseUploadStage((prev) => {
        const next = { ...prev };
        delete next[caseIndex];
        return next;
      });
    }
  };

  // Advisor confirms a Layer 2 override. The cached file from the
  // failing attempt is re-submitted with override=true and the reason.
  const handleOverrideUpload = async (caseIndex, reason) => {
    const rowErr = caseRowErrors[caseIndex];
    if (!rowErr || !rowErr.cachedFile) return;
    setOverrideModal(null);
    await handleCaseScreenshotUpload(caseIndex, null, {
      fileBlob: rowErr.cachedFile,
      override: true,
      overrideReason: reason,
    });
  };

  // Advisor clears a Layer 3 flag from the flag detail modal.
  const handleClearFlag = async (caseIndex, reason) => {
    try {
      setActionLoading(true);
      await overrideFlag({
        fileRowId: file.id,
        caseIndex,
        reason,
        overrideBy: user?.email || '',
      });
      await reloadCaseScreenshots(file.id);
      setFlagModalCase(null);
    } catch (err) {
      console.error('Failed to clear flag:', err);
      setError(err.message || 'Failed to clear flag.');
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-case helpers — only meaningful when caseCount IS NOT NULL.
  // The hook runs before the view has loaded the record (file is null
  // until then), so the two reads of file.caseCount are null-guarded;
  // once the record is in hand every value is what the view computed.
  const hasCases = file ? file.caseCount != null : false;
  const screenshotsByIndex = caseScreenshots.reduce((acc, row) => {
    acc[row.caseIndex] = row;
    return acc;
  }, {});
  const uploadedCaseCount = caseScreenshots.filter(
    (s) => s.caseIndex < (file?.caseCount || 0)
  ).length;
  const allCaseScreenshotsUploaded =
    hasCases && uploadedCaseCount === file.caseCount;

  return {
    // load path (written by FileDetail.loadFile)
    caseScreenshots,
    setCaseScreenshots,
    verificationDiagnostic,
    setVerificationDiagnostic,
    // derived
    hasCases,
    screenshotsByIndex,
    uploadedCaseCount,
    allCaseScreenshotsUploaded,
    // per-row upload state
    caseUploadingIndex,
    caseUploadStage,
    caseRowErrors,
    dismissRowError: (idx) =>
      setCaseRowErrors((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      }),
    // handlers
    handleScreenshotUpload,
    handleCaseScreenshotUpload,
    handleOverrideUpload,
    handleClearFlag,
    // modals and lightbox
    overrideModal,
    requestOverride: (idx) => setOverrideModal({ caseIndex: idx }),
    closeOverride: () => setOverrideModal(null),
    flagModalCase,
    openFlagDetails: (shot) => setFlagModalCase(shot),
    closeFlagDetails: () => setFlagModalCase(null),
    lightboxUrl,
    openLightbox: (url) => setLightboxUrl(url),
    closeLightbox: () => setLightboxUrl(null),
    // Adviser diagnostics
    diagnosticDismissed,
    dismissDiagnostic: () => setDiagnosticDismissed(true),
    showAggregateDiagnostics,
    openAggregateDiagnostics: () => setShowAggregateDiagnostics(true),
    closeAggregateDiagnostics: () => setShowAggregateDiagnostics(false),
  };
}
